import { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import * as signalR from "@microsoft/signalr";
import useInteractiveNotifications from "hooks/useInteractiveNotifications";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const useGetDataCartRealtime = ({ agentId, projectId, areaId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const { showNotification } = useInteractiveNotifications();

  const connectionRef = useRef(null);
  const prevRoomParams = useRef({
    agentId: null,
    projectId: null,
    areaId: null,
  });
  const isMountedRef = useRef(false);

  const token = Cookies.get("tokenSignalR");
  const role = Cookies.get("role");

  const connect = async () => {
    if (connectionRef.current) return;

    const baseUrl = process.env.REACT_APP_API;
    const endpoint =
      Number(role) === 4
        ? `signalr-shopping-cart?enc_auth_token=${encodeURIComponent(token)}`
        : `signalr-shopping-cart-agent?enc_auth_token=${encodeURIComponent(
            token
          )}`;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}${endpoint}`)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connectionRef.current = connection;

    if (Number(role) === 4) {
      connection.on("JoinRoomConfirmation", (message) => {
        if (!isMountedRef.current) return;
        setData(message);
      });
      connection.on("ReceiveShoppingCartUpdate", (message) => {
        if (!isMountedRef.current) return;
        setData(message);
      });
      connection.on("HaveNewPropertyRegister", (message) => {
        if (!isMountedRef.current) return;
        const time = dayjs(message.registerDateTime).format("HH:mm:ss");
        const notificationMsg = t("cart.property_register_notification", {
          time,
          agentName: message.agentName,
          propertyNo: message.propertyNo,
        });
        showNotification(notificationMsg);
      });
    } else {
      connection.on("JoinAvailableAgentRoomConfirmation", (message) => {
        if (!isMountedRef.current) return;
        setData(message);
      });
      connection.on("ReceiveShoppingCartAvailableAgentUpdate", (message) => {
        if (!isMountedRef.current) return;
        setData(message);
      });
    }

    try {
      await connection.start();
      setConnected(true);
    } catch (err) {
      setError(true);
      setErrorMessage(err?.message || "Connection failed");
    }
  };

  const disconnect = async () => {
    if (connectionRef.current) {
      await connectionRef.current.stop();
      connectionRef.current = null;
      setConnected(false);
    }
  };

  const joinRoom = async (agentId, projectId, areaId) => {
    if (!connectionRef.current) return;
    try {
      Number(role) === 4
        ? await connectionRef.current.invoke(
            "JoinBlockProjectRoom",
            projectId,
            areaId,
            agentId
          )
        : await connectionRef.current.invoke(
            "JoinAvailableAgentRoom",
            projectId,
            areaId,
            agentId
          );
      prevRoomParams.current = { agentId, projectId, areaId };
    } catch (err) {
      setError(true);
      setErrorMessage(err?.message || "Join room failed");
    } finally {
      setLoading(false);
    }
  };

  const leaveRoom = async (agentId, projectId, areaId) => {
    if (!connectionRef.current) return;
    try {
      Number(role) === 4
        ? await connectionRef.current.invoke(
            "LeaveBlockProjectRoom",
            projectId,
            areaId,
            agentId
          )
        : await connectionRef.current.invoke(
            "LeaveAvailableAgentRoom",
            projectId,
            areaId,
            agentId
          );
    } catch (err) {
      console.error("❌ Leave room error:", err);
      setError(true);
      setErrorMessage(err?.message || "Leave room failed");
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    connect();
    return () => {
      isMountedRef.current = false;
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (connected && projectId && areaId) {
      joinRoom(agentId, projectId, areaId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]); // chạy ngay khi connect xong

  // Handle join/leave when params change
  useEffect(() => {
    if (!connected) return;

    const hasAllParams = projectId;
    const roomChanged =
      prevRoomParams.current.agentId !== agentId ||
      prevRoomParams.current.projectId !== projectId ||
      prevRoomParams.current.areaId !== areaId;

    const prev = prevRoomParams.current;

    const processRoomChange = async () => {
      if (roomChanged) {
        if (prev.projectId && prev.areaId) {
          await leaveRoom(prev.agentId, prev.projectId, prev.areaId);
        }
        if (hasAllParams) {
          setLoading(true);
          await joinRoom(agentId, projectId, areaId);
        }
      }
    };

    processRoomChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, projectId, areaId, connected]);

  return { data, loading, connected, error, errorMessage, disconnect };
};

export default useGetDataCartRealtime;
